import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface SlackChannel {
  id: string;
  name: string;
  created: number;
  creator: string;
  is_archived: boolean;
  num_members: number;
}

/**
 * Fetch recent Slack channels via slack-list-channels action test endpoint.
 * Used as a fallback for slack-new-channel-created trigger.
 *
 * Strategy:
 * - Fetch channels
 * - Transform each channel into a trigger result matching the resultSchema
 * - Filter out archived channels
 *
 * Uses bot token (no user scope needed).
 */
export const slackChannelCreatedFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { connectorId } = ctx;

  if (!connectorId) return [];

  const response = await authenticatedFetch<{
    data: {
      result: {
        success: boolean;
        data: {
          ret: {
            ok: boolean;
            channels: SlackChannel[];
          };
        };
      };
    };
  }>(`${V2_ENDPOINT}/v1/actions/slack-list-channels/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: { limit: 50 },
      connector: { id: connectorId, type: "slack" },
    }),
  });

  if (!response.ok) return [];
  const json = await response.json();

  const channels = (json.data?.result?.data?.ret?.channels ?? []) as SlackChannel[];
  if (channels.length === 0) return [];

  // Sort by created desc (newest first) and filter out archived
  const sorted = channels
    .filter(ch => !ch.is_archived)
    .sort((a, b) => b.created - a.created)
    .slice(0, 10);

  return sorted.map(ch => ({
    triggeredAt: new Date(ch.created * 1000).toISOString(),
    eventType: "channel_created",
    channel: {
      id: ch.id,
      name: ch.name,
      created: ch.created,
      creator: ch.creator,
    },
    // Metadata for UI display
    _channelName: ch.name,
    _creatorId: ch.creator,
    _memberCount: ch.num_members,
    _createdAt: new Date(ch.created * 1000).toLocaleDateString(),
  }));
};
